import { AirportShuttle, Email, FileUpload, VerifiedUser } from "@mui/icons-material";
import { Divider, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import { ActivityListItem, ActivityListItemType } from "@safelease/service-utilities";

type ActivityItemListProps = {
  activityListQuery: UseQueryResult<ActivityListItem[]>;
};

function ActivityItemList({ activityListQuery }: ActivityItemListProps) {
  const { data: activityList, isError } = activityListQuery;

  const getPrimaryText = (activity: ActivityListItem) => {
    switch (activity.type) {
      case ActivityListItemType.MOVE_IN:
        return "Move-in";
      case ActivityListItemType.PRIVATE_POLICY_UPLOADED:
        return `Private policy uploaded in ${activity.submittedBy === "tenant" ? "Tenant Portal" : "Safelease dashboard"}`;
      case ActivityListItemType.PRIVATE_POLICY_SUCCESSFULLY_ATTACHED_EMAIL_SENT:
        return "Email sent for Accepted policy";
      case ActivityListItemType.PRIVATE_POLICY_SUCCESSFULLY_UPLOADED_EMAIL_SENT:
        return "Email sent for Submitted policy";
      case ActivityListItemType.PRIVATE_POLICY_REJECTED_EMAIL_SENT:
        return "Email sent for Rejected policy";
      case ActivityListItemType.PRIVATE_POLICY_EXPIRATION_REMINDER_EMAIL_SENT:
        return "Email sent for policy expiration reminder";
      case ActivityListItemType.SAFELEASE_POLICY_ENROLLED:
        return "Enrolled in Safelease plan";
    }
  };

  const getIcon = (activity: ActivityListItem) => {
    switch (activity.type) {
      case ActivityListItemType.MOVE_IN:
        return <AirportShuttle />;
      case ActivityListItemType.PRIVATE_POLICY_SUCCESSFULLY_ATTACHED_EMAIL_SENT:
      case ActivityListItemType.PRIVATE_POLICY_SUCCESSFULLY_UPLOADED_EMAIL_SENT:
      case ActivityListItemType.PRIVATE_POLICY_REJECTED_EMAIL_SENT:
      case ActivityListItemType.PRIVATE_POLICY_EXPIRATION_REMINDER_EMAIL_SENT:
        return <Email />;
      case ActivityListItemType.PRIVATE_POLICY_UPLOADED:
        return <FileUpload />;
      case ActivityListItemType.SAFELEASE_POLICY_ENROLLED:
        return <VerifiedUser />;
    }
  };

  if (!activityList || isError) {
    enqueueSnackbar("Failed to load occupancy details", { variant: "error" });
    return <Typography>ERROR: Failed to load occupancy details</Typography>;
  }
  const sortedActivityList = activityList.sort((a, b) => {
    return dayjs(b.date).valueOf() - dayjs(a.date).valueOf();
  });
  return (
    <Stack>
      <Typography sx={{ padding: 1.5, fontWeight: 500 }}>Activity</Typography>
      <Divider />
      <List>
        {sortedActivityList.map((activity) => (
          <ListItem key={activity.id}>
            <ListItemIcon>{getIcon(activity)}</ListItemIcon>
            <ListItemText primary={getPrimaryText(activity)} secondary={dayjs(activity.date).format("MM/DD/YYYY")} />
          </ListItem>
        ))}
      </List>
    </Stack>
  );
}

export { ActivityItemList };
